import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    companyName: '',
    legalName: '',
    companyEmail: '',
    companyAddress: '',
    managerFirstName: '',
    managerMiddleName: '',
    managerLastName: '',
    managerEmail: '',
    managerMobile: '',
    employeeId: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (
      !formData.companyName ||
      !formData.legalName ||
      !formData.companyEmail ||
      !formData.companyAddress ||
      !formData.managerFirstName ||
      !formData.managerLastName ||
      !formData.managerEmail ||
      !formData.managerMobile ||
      !formData.employeeId ||
      !formData.password
    ) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    const result = await signup(formData);
    setSubmitting(false);
    if (!result?.success) {
      setError(result?.error || 'Signup failed. Please try again.');
      return;
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-[#0D1F2D] flex items-start justify-center relative overflow-x-hidden overflow-y-auto py-12">
      {/* Background Decorative Elements */}
      <div className="absolute left-0 top-0 w-96 h-96 rounded-full bg-blue-600 opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute right-0 top-0 w-64 h-64 bg-orange-500 opacity-30" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }}></div>
      
      {/* Header */}
      <div className="absolute top-6 left-6">
        <div className="flex items-center space-x-2">
          <svg className="w-8 h-8 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
          </svg>
          <span className="text-white text-xl font-bold">ApurvaX</span>
          <span className="text-gray-400 text-xl">Payroll</span>
        </div>
      </div>

      {/* Signup Card */}
      <div className="w-full max-w-xl px-6 relative z-10">
        <div className="bg-[#1E2A34] rounded-lg p-8 shadow-2xl">
          <h2 className="text-2xl font-semibold text-white mb-2 text-center">
            Get started with ApurvaX Payroll
          </h2>
          <p className="text-gray-400 text-sm text-center mb-6">Create your account to manage payroll efficiently</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-white font-medium">Company Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="companyName" className="text-gray-300 text-sm">Company Name</Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    type="text"
                    placeholder="Enter company name"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="legalName" className="text-gray-300 text-sm">Legal Name</Label>
                  <Input
                    id="legalName"
                    name="legalName"
                    type="text"
                    placeholder="Enter legal name"
                    value={formData.legalName}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="companyEmail" className="text-gray-300 text-sm">Company Email</Label>
                  <Input
                    id="companyEmail"
                    name="companyEmail"
                    type="email"
                    placeholder="Enter company email"
                    value={formData.companyEmail}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="companyAddress" className="text-gray-300 text-sm">Company Address</Label>
                  <Input
                    id="companyAddress"
                    name="companyAddress"
                    type="text"
                    placeholder="Enter company address"
                    value={formData.companyAddress}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t border-gray-700 pt-5">
              <h3 className="text-white font-medium">Manager Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="managerFirstName" className="text-gray-300 text-sm">First Name</Label>
                  <Input
                    id="managerFirstName"
                    name="managerFirstName"
                    type="text"
                    placeholder="Enter first name"
                    value={formData.managerFirstName}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="managerMiddleName" className="text-gray-300 text-sm">Middle Name</Label>
                  <Input
                    id="managerMiddleName"
                    name="managerMiddleName"
                    type="text"
                    placeholder="Enter middle name"
                    value={formData.managerMiddleName}
                    onChange={handleChange}
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="managerLastName" className="text-gray-300 text-sm">Last Name</Label>
                  <Input
                    id="managerLastName"
                    name="managerLastName"
                    type="text"
                    placeholder="Enter last name"
                    value={formData.managerLastName}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="managerEmail" className="text-gray-300 text-sm">Email</Label>
                  <Input
                    id="managerEmail"
                    name="managerEmail"
                    type="email"
                    placeholder="Enter manager email"
                    value={formData.managerEmail}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="managerMobile" className="text-gray-300 text-sm">Mobile</Label>
                  <Input
                    id="managerMobile"
                    name="managerMobile"
                    type="tel"
                    placeholder="Enter mobile number"
                    value={formData.managerMobile}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="employeeId" className="text-gray-300 text-sm">Employee ID</Label>
                  <Input
                    id="employeeId"
                    name="employeeId"
                    type="text"
                    placeholder="Enter employee ID"
                    value={formData.employeeId}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t border-gray-700 pt-5">
              <h3 className="text-white font-medium">Account Security</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password" className="text-gray-300 text-sm">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-gray-300 text-sm">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="mt-1 bg-[#0D1F2D] border-gray-600 text-white placeholder:text-gray-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="text-red-400 text-sm">{error}</div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-medium transition-colors"
            >
              {submitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-gray-400 text-sm">Already have an account? </span>
            <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
              Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
